import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useApp } from "./AppContext";
import {
  getFullTourSteps,
  getPageTourSteps,
  getPageTours,
} from "../data/liveTourSteps";
import { findTourTarget } from "../components/help/liveTourDom";

const LiveTourContext = createContext(null);

const TARGET_WAIT_MS = 15000;
const OPTIONAL_WAIT_MS = 1800;
const TARGET_POLL_MS = 150;
const NAV_SETTLE_MS = 500;

export const ASSESSMENT_TOUR_ROUTES = [
  "/user/gap-assessment",
  "/user/ropa/manual",
  "/user/pia",
  "/user/tprm",
];

/** Targets that exist in the page header before a department is selected. */
const HEADER_TOUR_TARGETS = new Set([
  "tour-gap-header",
  "tour-gap-department",
  "tour-gap-view-table",
  "tour-ropa-header",
  "tour-ropa-department",
  "tour-ropa-stats",
  "tour-ropa-view-table",
  "tour-ropa-submit-btn",
  "tour-pia-header",
  "tour-pia-department",
  "tour-pia-progress",
  "tour-pia-view-table",
  "tour-pia-submit",
  "tour-tprm-header",
  "tour-tprm-department",
  "tour-tprm-view-table",
  "tour-tprm-draft",
]);

export function notifyLiveTourTargetsChanged() {
  window.dispatchEvent(new CustomEvent("live-tour:targets-changed"));
}

export function getLiveTourPreviewDepartmentId(
  liveTour,
  departments,
  departmentsLoading,
) {
  if (!liveTour?.active || departmentsLoading || !departments?.length) {
    return "";
  }
  if (
    typeof window !== "undefined" &&
    ASSESSMENT_TOUR_ROUTES.includes(window.location.pathname)
  ) {
    return departments[0].id;
  }
  const targetId = liveTour.currentStep?.targetId;
  const needsForm = targetId && !HEADER_TOUR_TARGETS.has(targetId);
  if (!needsForm && liveTour.stepIndex < 2) return "";
  return departments[0].id;
}

/**
 * While a live tour runs on Gap / ROPA / PIA / TPRM, pick the first department
 * so sidebar and form targets mount and the spotlight can attach.
 */
export function useLiveTourDepartmentPrep(
  departmentId,
  setDepartmentId,
  departments,
  departmentsLoading,
  { enabled = true } = {},
) {
  const liveTour = useLiveTourOptional();
  const previewId = getLiveTourPreviewDepartmentId(
    liveTour,
    departments,
    departmentsLoading,
  );

  useEffect(() => {
    if (!enabled || !liveTour?.active || !previewId || departmentId) return;
    setDepartmentId(previewId);
  }, [
    enabled,
    liveTour?.active,
    previewId,
    departmentId,
    setDepartmentId,
  ]);

  useEffect(() => {
    if (!enabled || !liveTour?.active) return;
    if (!departmentId && !previewId) return;
    const timer = window.setTimeout(notifyLiveTourTargetsChanged, 350);
    return () => window.clearTimeout(timer);
  }, [enabled, liveTour?.active, departmentId, previewId]);
}

export function LiveTourProvider({ children }) {
  const { currentUser } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const role = currentUser?.role || "user";
  const allowedModules = currentUser?.modules || [];

  const allSteps = useMemo(
    () => getFullTourSteps(role, allowedModules),
    [role, allowedModules]
  );
  const pageTours = useMemo(
    () => getPageTours(role, allowedModules),
    [role, allowedModules]
  );

  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [targetReady, setTargetReady] = useState(false);
  const [targetMissing, setTargetMissing] = useState(false);
  const [steps, setSteps] = useState(allSteps);
  const [tourLabel, setTourLabel] = useState("Full platform tour");
  const [activePageId, setActivePageId] = useState(null);
  const [targetPollGeneration, setTargetPollGeneration] = useState(0);

  const navigatingRef = useRef(false);
  const stepDirectionRef = useRef("forward");
  const nextStepRef = useRef(null);
  const advanceLockRef = useRef(false);
  const pollGenRef = useRef(0);

  const currentStep = active ? steps[stepIndex] : null;
  const waitingForTarget = active && !!currentStep && !targetReady && !targetMissing;

  const goToStep = useCallback(
    (index, stepsList = steps) => {
      if (index < 0 || index >= stepsList.length) return;
      setStepIndex(index);
      setTargetReady(false);
      setTargetMissing(false);
      const step = stepsList[index];
      const here =
        typeof window !== "undefined"
          ? window.location.pathname
          : location.pathname;
      const needsNav = Boolean(step?.route && here !== step.route);
      navigatingRef.current = needsNav;
      if (needsNav) navigate(step.route);
    },
    [steps, location.pathname, navigate]
  );

  const startTour = useCallback(
    (fromIndex = 0) => {
      advanceLockRef.current = true;
      setSteps(allSteps);
      setTourLabel("Full platform tour");
      setActivePageId(null);
      stepDirectionRef.current = "forward";
      setActive(true);
      goToStep(fromIndex, allSteps);
    },
    [allSteps, goToStep]
  );

  const startPageTour = useCallback(
    (pageId) => {
      const pageSteps = getPageTourSteps(pageId, role, allowedModules);
      if (!pageSteps.length) return;
      const meta = pageTours.find((p) => p.id === pageId);
      advanceLockRef.current = true;
      setSteps(pageSteps);
      setTourLabel(meta?.title || "Page tour");
      setActivePageId(pageId);
      stepDirectionRef.current = "forward";
      setActive(true);
      goToStep(0, pageSteps);
    },
    [role, allowedModules, pageTours, goToStep]
  );

  const endTour = useCallback(() => {
    advanceLockRef.current = false;
    navigatingRef.current = false;
    pollGenRef.current += 1;
    setActive(false);
    setStepIndex(0);
    setTargetReady(false);
    setTargetMissing(false);
    setSteps(allSteps);
    setTourLabel("Full platform tour");
    setActivePageId(null);
    document
      .querySelectorAll(".live-tour-target-active")
      .forEach((el) => el.classList.remove("live-tour-target-active"));
  }, [allSteps]);

  const nextStep = useCallback(
    ({ force = false } = {}) => {
      if (!force && advanceLockRef.current) return;
      if (!force && waitingForTarget) return;
      stepDirectionRef.current = "forward";
      if (stepIndex >= steps.length - 1) {
        endTour();
        return;
      }
      advanceLockRef.current = true;
      goToStep(stepIndex + 1);
    },
    [stepIndex, steps.length, goToStep, endTour, waitingForTarget]
  );

  nextStepRef.current = nextStep;

  const prevStep = useCallback(() => {
    if (advanceLockRef.current || waitingForTarget) return;
    if (stepIndex <= 0) return;
    stepDirectionRef.current = "back";
    advanceLockRef.current = true;
    goToStep(stepIndex - 1);
  }, [stepIndex, goToStep, waitingForTarget]);

  const currentStepRef = useRef(null);
  currentStepRef.current = currentStep;

  useEffect(() => {
    if (targetReady || targetMissing) {
      advanceLockRef.current = false;
    }
  }, [targetReady, targetMissing, stepIndex]);

  useEffect(() => {
    const onTargetsChanged = () => {
      const id = currentStepRef.current?.targetId;
      if (id && findTourTarget(id)) return;
      setTargetReady(false);
      setTargetMissing(false);
      setTargetPollGeneration((n) => n + 1);
    };
    window.addEventListener("live-tour:targets-changed", onTargetsChanged);
    return () =>
      window.removeEventListener("live-tour:targets-changed", onTargetsChanged);
  }, []);

  useEffect(() => {
    if (!active || !currentStep) return undefined;

    if (currentStep.goToLastSection) {
      window.dispatchEvent(new CustomEvent("live-tour:go-to-last-section"));
      const t1 = window.setTimeout(notifyLiveTourTargetsChanged, 500);
      const t2 = window.setTimeout(notifyLiveTourTargetsChanged, 950);
      return () => {
        window.clearTimeout(t1);
        window.clearTimeout(t2);
      };
    }

    if (currentStep.tourEvent) {
      window.dispatchEvent(
        new CustomEvent(currentStep.tourEvent, {
          detail: currentStep.tourEventDetail || {},
        }),
      );
      const t1 = window.setTimeout(notifyLiveTourTargetsChanged, 400);
      const t2 = window.setTimeout(notifyLiveTourTargetsChanged, 850);
      return () => {
        window.clearTimeout(t1);
        window.clearTimeout(t2);
      };
    }

    return undefined;
  }, [active, currentStep, stepIndex]);

  useEffect(() => {
    if (!active || !currentStep) return undefined;

    const myGen = ++pollGenRef.current;
    let cancelled = false;
    const started = Date.now();
    const waitMs = currentStep.optional
      ? currentStep.optionalWaitMs || OPTIONAL_WAIT_MS
      : TARGET_WAIT_MS;
    const delay =
      currentStep.goToLastSection || currentStep.tourEvent
        ? NAV_SETTLE_MS + 900
        : navigatingRef.current
          ? NAV_SETTLE_MS
          : 120;

    const stillCurrent = () =>
      !cancelled && myGen === pollGenRef.current;

    const poll = () => {
      if (!stillCurrent()) return;
      const el = findTourTarget(currentStep.targetId);
      if (el) {
        setTargetReady(true);
        setTargetMissing(false);
        navigatingRef.current = false;
        const instant = stepDirectionRef.current === "back";
        el.scrollIntoView({
          block:
            currentStep?.forceDock ||
            (el.getBoundingClientRect().height >
              window.innerHeight * 0.32)
              ? "start"
              : "center",
          behavior: instant ? "instant" : "smooth",
        });
        return;
      }
      if (Date.now() - started > waitMs) {
        if (currentStep.optional && nextStepRef.current) {
          navigatingRef.current = false;
          nextStepRef.current({ force: true });
          return;
        }
        setTargetMissing(true);
        setTargetReady(false);
        navigatingRef.current = false;
        return;
      }
      window.setTimeout(poll, TARGET_POLL_MS);
    };

    const kickoff = window.setTimeout(poll, delay);
    return () => {
      cancelled = true;
      window.clearTimeout(kickoff);
    };
  }, [active, currentStep, location.pathname, stepIndex, targetPollGeneration]);

  const value = useMemo(
    () => ({
      active,
      steps,
      stepIndex,
      currentStep,
      totalSteps: steps.length,
      targetReady,
      targetMissing,
      waitingForTarget,
      tourLabel,
      activePageId,
      pageTours,
      startTour,
      startPageTour,
      endTour,
      nextStep,
      prevStep,
      goToStep,
    }),
    [
      active,
      steps,
      stepIndex,
      currentStep,
      targetReady,
      targetMissing,
      waitingForTarget,
      tourLabel,
      activePageId,
      pageTours,
      startTour,
      startPageTour,
      endTour,
      nextStep,
      prevStep,
      goToStep,
    ]
  );

  return (
    <LiveTourContext.Provider value={value}>{children}</LiveTourContext.Provider>
  );
}

export function useLiveTour() {
  const ctx = useContext(LiveTourContext);
  if (!ctx) {
    throw new Error("useLiveTour must be used within LiveTourProvider");
  }
  return ctx;
}

export function useLiveTourOptional() {
  return useContext(LiveTourContext);
}
